import React, { useRef, useEffect, useState } from 'react';
import './RichTextEditor.css';

const RichTextEditor = ({ value, onChange, placeholder, style = {} }) => {
  const editorRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(null); // 'text' or 'background'
  const [showFontSize, setShowFontSize] = useState(false);

  // Clean HTML content - decode entities and ensure proper formatting
  const cleanHtml = (html) => {
    if (!html || typeof html !== 'string') return '';
    
    let cleaned = html.trim();
    
    // Decode HTML entities if present (like &lt; becomes <)
    // Use a temporary textarea to decode entities properly
    if (cleaned.includes('&')) {
      const textarea = document.createElement('textarea');
      textarea.innerHTML = cleaned;
      cleaned = textarea.value;
    }
    
    // If content is plain text without HTML tags, convert line breaks to paragraphs
    if (cleaned && !cleaned.match(/<[^>]+>/)) {
      const lines = cleaned.split(/\r?\n/).filter(line => line.trim());
      if (lines.length > 0) {
        cleaned = lines.map(line => `<p>${line.trim()}</p>`).join('');
      } else {
        cleaned = '';
      }
    }
    
    // Remove script tags for security
    cleaned = cleaned.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
    
    return cleaned;
  };

  useEffect(() => {
    if (editorRef.current) {
      const cleanedValue = cleanHtml(value || '');
      // Only update if content is different and editor is not focused
      if (!isFocused && editorRef.current.innerHTML !== cleanedValue) {
        // Save cursor position if possible
        const selection = window.getSelection();
        const range = selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
        const cursorPosition = range ? range.startOffset : null;
        
        editorRef.current.innerHTML = cleanedValue;
        setIsInitialized(true);
        
        // Restore cursor position if possible
        if (range && cursorPosition !== null && editorRef.current.firstChild) {
          try {
            const newRange = document.createRange();
            newRange.setStart(editorRef.current.firstChild, Math.min(cursorPosition, editorRef.current.firstChild.textContent.length));
            newRange.collapse(true);
            selection.removeAllRanges();
            selection.addRange(newRange);
          } catch (e) {
            // Ignore cursor restoration errors
          }
        }
      }
    }
  }, [value, isFocused]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showColorPicker || showFontSize) {
        const target = event.target;
        if (!target.closest('.rich-text-toolbar')) {
          setShowColorPicker(null);
          setShowFontSize(false);
        }
      }
    };

    if (showColorPicker || showFontSize) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showColorPicker, showFontSize]);

  const handleInput = (e) => {
    if (editorRef.current) {
      const html = editorRef.current.innerHTML;
      // Clean up empty paragraphs and ensure proper formatting
      let cleanedHtml = html
        .replace(/<p><br><\/p>/g, '<p></p>')
        .replace(/<p>\s*<\/p>/g, '')
        .trim();
      
      // If empty, return empty string
      if (!cleanedHtml || cleanedHtml === '<p></p>' || cleanedHtml === '<br>') {
        cleanedHtml = '';
      }
      
      onChange(cleanedHtml);
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  const execCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    // Trigger onChange manually
    if (editorRef.current) {
      const event = new Event('input', { bubbles: true });
      editorRef.current.dispatchEvent(event);
    }
  };

  const handleColorChange = (color, type) => {
    execCommand(type === 'text' ? 'foreColor' : 'backColor', color);
    setShowColorPicker(null);
  };

  const handleImage = () => {
    const url = prompt('Enter image URL:');
    if (url) {
      execCommand('insertImage', url);
    }
  };

  const handleFontSize = (size) => {
    execCommand('fontSize', size);
    setShowFontSize(false);
  };

  const toolbarButtons = [
    { command: 'bold', icon: 'fa-bold', title: 'Bold' },
    { command: 'italic', icon: 'fa-italic', title: 'Italic' },
    { command: 'underline', icon: 'fa-underline', title: 'Underline' },
    { command: 'strikeThrough', icon: 'fa-strikethrough', title: 'Strikethrough' },
    { separator: true },
    { command: 'insertUnorderedList', icon: 'fa-list-ul', title: 'Bullet List' },
    { command: 'insertOrderedList', icon: 'fa-list-ol', title: 'Numbered List' },
    { command: 'outdent', icon: 'fa-outdent', title: 'Decrease Indent' },
    { command: 'indent', icon: 'fa-indent', title: 'Increase Indent' },
    { separator: true },
    { command: 'formatBlock', value: '<h1>', icon: 'fa-heading', title: 'Heading 1', text: 'H1' },
    { command: 'formatBlock', value: '<h2>', icon: 'fa-heading', title: 'Heading 2', text: 'H2' },
    { command: 'formatBlock', value: '<h3>', icon: 'fa-heading', title: 'Heading 3', text: 'H3' },
    { command: 'formatBlock', value: '<h4>', icon: 'fa-heading', title: 'Heading 4', text: 'H4' },
    { separator: true },
    { command: 'justifyLeft', icon: 'fa-align-left', title: 'Align Left' },
    { command: 'justifyCenter', icon: 'fa-align-center', title: 'Align Center' },
    { command: 'justifyRight', icon: 'fa-align-right', title: 'Align Right' },
    { command: 'justifyFull', icon: 'fa-align-justify', title: 'Justify' },
    { separator: true },
    { command: 'formatBlock', value: '<blockquote>', icon: 'fa-quote-right', title: 'Quote' },
    { command: 'createLink', icon: 'fa-link', title: 'Insert Link' },
    { command: 'insertImage', icon: 'fa-image', title: 'Insert Image', custom: true },
    { separator: true },
    { command: 'foreColor', icon: 'fa-font', title: 'Text Color', custom: true, type: 'text' },
    { command: 'backColor', icon: 'fa-fill-drip', title: 'Background Color', custom: true, type: 'background' },
    { command: 'fontSize', icon: 'fa-text-height', title: 'Font Size', custom: true },
    { separator: true },
    { command: 'removeFormat', icon: 'fa-eraser', title: 'Remove Formatting' },
  ];

  const colors = [
    '#000000', '#333333', '#666666', '#999999', '#CCCCCC', '#FFFFFF',
    '#FF0000', '#FF6600', '#FFCC00', '#99CC00', '#00CC00', '#00CCCC',
    '#0066FF', '#0000FF', '#6600FF', '#CC00FF', '#FF00CC', '#FF0066',
    '#96ca2d', '#7fb800', '#428309'
  ];

  const handleLink = () => {
    const url = prompt('Enter URL:');
    if (url) {
      execCommand('createLink', url);
    }
  };

  const handleBlockquote = () => {
    execCommand('formatBlock', '<blockquote>');
  };

  return (
    <div 
      className={`rich-text-editor ${isFocused ? 'focused' : ''}`}
      style={style}
    >
      {/* Toolbar */}
      <div className="rich-text-toolbar">
        {toolbarButtons.map((btn, index) => {
          if (btn.separator) {
            return <div key={`sep-${index}`} className="toolbar-separator" />;
          }
          
          if (btn.command === 'createLink') {
            return (
              <button
                key={index}
                type="button"
                className="toolbar-btn"
                onClick={handleLink}
                title={btn.title}
              >
                <i className={`fas ${btn.icon}`} />
              </button>
            );
          }

          if (btn.command === 'insertImage' && btn.custom) {
            return (
              <button
                key={index}
                type="button"
                className="toolbar-btn"
                onClick={handleImage}
                title={btn.title}
              >
                <i className={`fas ${btn.icon}`} />
              </button>
            );
          }

          if (btn.command === 'formatBlock' && btn.value === '<blockquote>') {
            return (
              <button
                key={index}
                type="button"
                className="toolbar-btn"
                onClick={handleBlockquote}
                title={btn.title}
              >
                <i className={`fas ${btn.icon}`} />
              </button>
            );
          }

          if (btn.command === 'formatBlock') {
            return (
              <button
                key={index}
                type="button"
                className="toolbar-btn"
                onClick={() => execCommand(btn.command, btn.value)}
                title={btn.title}
              >
                {btn.text || <i className={`fas ${btn.icon}`} />}
              </button>
            );
          }

          if (btn.command === 'foreColor' || btn.command === 'backColor') {
            return (
              <div key={index} style={{ position: 'relative', display: 'inline-block' }}>
                <button
                  type="button"
                  className="toolbar-btn"
                  onClick={() => setShowColorPicker(showColorPicker === btn.type ? null : btn.type)}
                  title={btn.title}
                >
                  <i className={`fas ${btn.icon}`} />
                </button>
                {showColorPicker === btn.type && (
                  <div className="color-picker-dropdown">
                    <div className="color-picker-grid">
                      {colors.map((color, colorIndex) => (
                        <button
                          key={colorIndex}
                          type="button"
                          className="color-picker-item"
                          style={{ backgroundColor: color }}
                          onClick={() => handleColorChange(color, btn.type)}
                          title={color}
                        />
                      ))}
                    </div>
                    <input
                      type="color"
                      className="color-picker-input"
                      onChange={(e) => handleColorChange(e.target.value, btn.type)}
                    />
                  </div>
                )}
              </div>
            );
          }

          if (btn.command === 'fontSize' && btn.custom) {
            return (
              <div key={index} style={{ position: 'relative', display: 'inline-block' }}>
                <button
                  type="button"
                  className="toolbar-btn"
                  onClick={() => setShowFontSize(!showFontSize)}
                  title={btn.title}
                >
                  <i className={`fas ${btn.icon}`} />
                </button>
                {showFontSize && (
                  <div className="font-size-dropdown">
                    {[1, 2, 3, 4, 5, 6, 7].map((size) => (
                      <button
                        key={size}
                        type="button"
                        className="font-size-item"
                        onClick={() => handleFontSize(size)}
                      >
                        {size}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          }

          return (
            <button
              key={index}
              type="button"
              className="toolbar-btn"
              onClick={() => execCommand(btn.command)}
              title={btn.title}
            >
              <i className={`fas ${btn.icon}`} />
            </button>
          );
        })}
      </div>

      {/* Editor */}
      <div
        ref={editorRef}
        contentEditable
        className="rich-text-content"
        onInput={handleInput}
        onFocus={handleFocus}
        onBlur={(e) => {
          handleBlur();
          // Clean up on blur
          if (editorRef.current) {
            const html = editorRef.current.innerHTML;
            let cleanedHtml = html
              .replace(/<p><br><\/p>/g, '')
              .replace(/<p>\s*<\/p>/g, '')
              .trim();
            
            if (!cleanedHtml || cleanedHtml === '<p></p>' || cleanedHtml === '<br>') {
              cleanedHtml = '';
            }
            
            if (editorRef.current.innerHTML !== cleanedHtml) {
              editorRef.current.innerHTML = cleanedHtml;
              onChange(cleanedHtml);
            }
          }
        }}
        data-placeholder={placeholder || 'Enter content...'}
        suppressContentEditableWarning
      />
    </div>
  );
};

export default RichTextEditor;

